import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { ROLE_LABELS } from "@/lib/constants";
import { LayoutDashboard, PackagePlus, Truck, Menu, ScanLine, LogOut, KeyRound, MonitorSmartphone, Sun, Moon, X } from "lucide-react";

const TABS = [
  { to: "/", label: "Home", icon: LayoutDashboard, perm: "nav.dashboard" },
  { to: "/inward", label: "Inward", icon: PackagePlus, perm: "nav.inward" },
  null,
  { to: "/outward", label: "Dispatch", icon: Truck, perm: "nav.outward" },
];

const tabCls = ({ isActive }) => `flex flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-semibold ${isActive ? "text-primary" : "text-muted-foreground"}`;

export function MobileTabBar({ items, onScan, onPassword }) {
  const { can } = useAuth();
  const [menu, setMenu] = useState(false);
  return (
    <>
      <nav className="fixed inset-x-0 bottom-0 z-50 flex h-16 items-stretch border-t border-border bg-card/95 backdrop-blur-md md:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)", height: "calc(4rem + env(safe-area-inset-bottom))" }} data-testid="mobile-tabbar">
        {TABS.map((t, i) => t === null ? (
          <div key="scan" className="flex flex-1 items-start justify-center">
            <button onClick={onScan} data-testid="mobile-scan-btn" aria-label="Scan label"
              className="-mt-5 flex h-16 w-16 items-center justify-center rounded-full border-4 border-background bg-primary text-primary-foreground shadow-lg shadow-primary/40 active:scale-95 transition-transform">
              <ScanLine size={26} />
            </button>
          </div>
        ) : can(t.perm) ? (
          <NavLink key={t.to} to={t.to} end={t.to === "/"} className={tabCls} data-testid={`tab-${t.label.toLowerCase()}`}>
            <t.icon size={21} /> {t.label}
          </NavLink>
        ) : <div key={i} className="flex-1" />)}
        <button onClick={() => setMenu(true)} className="flex flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-semibold text-muted-foreground" data-testid="tab-menu">
          <Menu size={21} /> Menu
        </button>
      </nav>
      {menu && <MenuSheet items={items} onClose={() => setMenu(false)} onPassword={onPassword} />}
    </>
  );
}

function MenuSheet({ items, onClose, onPassword }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useUI();
  const row = "flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium active:bg-accent";
  return (
    <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm md:hidden" onClick={onClose} data-testid="mobile-menu">
      <div className="animate-in-up absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-2xl border-t border-border bg-card p-4"
        style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }} onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <div><div className="text-sm font-bold">{user.full_name}</div><div className="font-mono text-[10px] uppercase text-muted-foreground">{ROLE_LABELS[user.role]}</div></div>
          <button onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-accent" data-testid="mobile-menu-close"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-1">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={onClose} data-testid={`mnav-${n.to === "/" ? "dashboard" : n.to.slice(1).replace("/", "-")}`}
              className={({ isActive }) => `${row} ${isActive ? "bg-primary/15 text-primary" : ""}`}>
              <n.icon size={18} /> <span className="truncate">{n.label}</span>
            </NavLink>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-1 border-t border-border pt-3">
          <NavLink to="/devices" onClick={onClose} className={row} data-testid="mnav-devices"><MonitorSmartphone size={18} /> My devices</NavLink>
          <button onClick={() => { onClose(); onPassword(); }} className={row} data-testid="mnav-password"><KeyRound size={18} /> Password</button>
          <button onClick={toggleTheme} className={row} data-testid="mnav-theme">{theme === "dark" ? <Sun size={18} /> : <Moon size={18} />} {theme === "dark" ? "Light mode" : "Dark mode"}</button>
          <button onClick={logout} className={`${row} text-red-500`} data-testid="mnav-logout"><LogOut size={18} /> Sign out</button>
        </div>
      </div>
    </div>
  );
}
